import { redirect } from '@tanstack/react-router';
import { SURFACE_PUBLIC, SURFACE_ROLES } from '@/lib/gql/__generated__/surfaces';
import { sessionSettled } from './auth';
import { useSessionRoles } from './hooks';
import { type AppEntryRoutes, canEnterSurface, decideSurfaceEntry } from './routing';

const requiredRolesOf = (page: string): readonly string[] =>
  (SURFACE_ROLES as Readonly<Record<string, readonly string[] | undefined>>)[page] ?? [];

/**
 * `=== true`, not a truthy read: a route naming a screen this contract does not declare answers
 * `undefined`, and the honest reading of "the map has never heard of this page" is the closed one —
 * the same direction `requiredRolesOf` takes when it answers `[]` for a screen with no requirement.
 */
const isPublicSurface = (page: string): boolean =>
  (SURFACE_PUBLIC as Readonly<Record<string, boolean | undefined>>)[page] === true;

export async function guardSurface(entry: AppEntryRoutes, page: string): Promise<void> {
  const verdict = decideSurfaceEntry(
    await sessionSettled(),
    requiredRolesOf(page),
    entry,
    window.location.pathname,
    isPublicSurface(page),
  );
  if (!verdict.allowed) throw redirect({ to: verdict.to });
}

export function useCanEnterSurface(page: string): boolean {
  return canEnterSurface(requiredRolesOf(page), useSessionRoles());
}
